// Phase 67: Credit card installment plan helpers.
//
// An installment plan represents a fixed-amount charge that's paid off
// in equal monthly installments. The plan itself is a single Dexie row;
// the helper functions derive a virtual payment schedule on demand.
//
// Schema (lib/localDb.js v6):
//   {
//     local_uuid,                  // uuid
//     credit_card_local_uuid,      // FK → credit_cards.local_uuid
//     total_amount,                // e.g. 10000.00 (the original charge)
//     total_periods,               // e.g. 12 (number of monthly payments)
//     monthly_amount,              // total_amount / total_periods (computed)
//     start_month,                 // 'YYYY-MM' first payment month
//     ended,                       // boolean — true when last payment made
//     note,                        // free text — what was bought
//     category_id,                 // optional category FK
//     server_id, deleted,          // sync boilerplate
//     created_at, updated_at,
//   }
//
// Derived (NOT stored):
//   - next_payment_month — first month >= today.start_month that's unpaid
//   - periods_paid        — count of months elapsed since start_month
//   - periods_remaining   — total_periods - periods_paid
//   - amount_paid         — periods_paid * monthly_amount
//   - amount_remaining    — total_amount - amount_paid

/**
 * Generate the virtual payment schedule for a plan.
 * Returns an array of `{ index, month, amount }` for each period (1..total_periods).
 */
export function generateSchedule(plan) {
  if (!plan || !plan.start_month || !plan.total_periods || !plan.monthly_amount) {
    return [];
  }
  const [y0, m0] = plan.start_month.split('-').map(Number);
  const result = [];
  for (let i = 0; i < plan.total_periods; i++) {
    const monthDate = new Date(y0, m0 - 1 + i, 1);
    const yyyy = monthDate.getFullYear();
    const mm = String(monthDate.getMonth() + 1).padStart(2, '0');
    result.push({
      index: i + 1,
      month: `${yyyy}-${mm}`,
      amount: plan.monthly_amount,
    });
  }
  return result;
}

/**
 * Compute derived progress fields for a plan given today's month.
 */
export function computeInstallmentProgress(plan, today = new Date()) {
  if (!plan || !plan.start_month || !plan.total_periods) {
    return {
      periods_paid: 0,
      periods_remaining: 0,
      amount_paid: 0,
      amount_remaining: 0,
      next_payment_month: null,
      is_complete: false,
      current_period_index: 0,
    };
  }
  const ymNow = today.getFullYear() * 12 + today.getMonth();
  const [y0, m0] = plan.start_month.split('-').map(Number);
  const ymStart = y0 * 12 + (m0 - 1);
  // periods_paid = months from start_month up to (but not including) current month
  const periodsElapsed = Math.max(0, ymNow - ymStart);
  const periods_paid = Math.min(periodsElapsed, plan.total_periods);
  const periods_remaining = Math.max(0, plan.total_periods - periods_paid);
  const amount_paid = periods_paid * plan.monthly_amount;
  const amount_remaining = plan.total_amount - amount_paid;
  const next_period_index = periods_paid + 1;
  const is_complete = periods_paid >= plan.total_periods || plan.ended === true;
  let next_payment_month = null;
  if (!is_complete && next_period_index <= plan.total_periods) {
    const nextDate = new Date(y0, (m0 - 1) + periods_paid, 1);
    next_payment_month = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}`;
  }
  return {
    periods_paid,
    periods_remaining,
    amount_paid,
    amount_remaining,
    next_payment_month,
    is_complete,
    current_period_index: periods_paid,
  };
}

/**
 * Compute monthly amount = total / periods. Rounded to 2 decimals;
 * if rounding leaves a remainder, the LAST period absorbs it
 * (so e.g. 10001 / 3 = 3333.67 + 3333.67 + 3333.66).
 */
export function computeMonthlyAmount(total, periods) {
  if (!periods || periods <= 0) return 0;
  const base = Math.floor((total * 100) / periods) / 100;
  const remainder = Math.round((total - base * periods) * 100) / 100;
  return Math.round((base + remainder / periods) * 100) / 100;
}

/**
 * Format a YYYY-MM string into a human-readable label.
 * E.g. '2026-08' → '8月' (zh-Hant), 'Aug 2026' (en), '2026年08月' (zh-Hans).
 */
export function formatMonthLabel(ym, t) {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  if (t) {
    // Use i18n if provided (locale-aware formatting)
    return t(`months.short.${m}`, { year: y });
  }
  return `${y}-${String(m).padStart(2, '0')}`;
}
